import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModuleLink as Link } from '../../components/ModuleLink';
import Layout from '../../components/Layout';
import PeoplePicker from '../../components/PeoplePicker';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useApiClient, type ApiRequestError } from '../../api/client';
import { AMENDABLE_STATUSES, PLAN_DRAFT_STATUSES, POST_SIGNOFF_STATUSES, fmtDate, fmtDateTime, safeJson, statusBadgeClass } from '../../utils';
import type {
  AffectedStakeholder, AmendmentsResponse, Approver, ChangeAmendment, ChangeRequest, FieldChange, GraphUser, ImplementationPlan,
  StakeholderResponse,
} from '../../types';

const SCOPES = ['Single Dept', 'Multi Dept', 'Org-Wide'];
const APPROACHES = ['Pilot', 'Phased', 'Full Rollout'];
const REASON_HINT = 'Say why you are changing this: it is recorded and shown to the approvers.';

type Errors = Record<string, string>;
type Save = (values: Record<string, unknown>, reason: string) => Promise<boolean>;

// ── small building blocks ─────────────────────────────────────────────────────
function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <span className="form-error">
      <i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {message}
    </span>
  );
}

function TextArea({ id, label, value, onChange, error, required, hint, rows = 4 }: {
  id: string; label: string; value: string; onChange: (v: string) => void; error?: string; required?: boolean; hint?: string; rows?: number;
}) {
  return (
    <div className="form-group form-group--full">
      <label className="form-label" htmlFor={id}>{label} {required && <span className="required">*</span>}</label>
      {hint && <p className="form-hint">{hint}</p>}
      <textarea id={id} className={`form-control${error ? ' is-error' : ''}`} rows={rows} value={value} onChange={e => onChange(e.target.value)} />
      <FieldError message={error} />
    </div>
  );
}

function Pills({ options, value, onChange }: { options: string[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="pill-group">
      {options.map(o => (
        <button key={o} type="button" className={`pill-option${value === o ? ' is-selected' : ''}`} onClick={() => onChange(o)}>{o}</button>
      ))}
    </div>
  );
}

function PersonPill({ name, detail, onRemove }: { name: string; detail: string; onRemove?: () => void }) {
  return (
    <div className="user-pill">
      <div className="user-pill__avatar">{name.charAt(0).toUpperCase()}</div>
      <span className="user-pill__name">{name}</span>
      <span className="user-pill__dept">{detail}</span>
      {onRemove && (
        <button type="button" className="user-pill__remove" onClick={onRemove} aria-label={`Remove ${name}`}>
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/** The reason box and Save / Cancel that close every section editor. */
function SaveBar({ reason, setReason, error, saving, onSave, onCancel }: {
  reason: string; setReason: (v: string) => void; error?: string; saving: boolean; onSave: () => void; onCancel: () => void;
}) {
  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
      <TextArea id="amend-reason" label="Reason for this change" required rows={2} hint={REASON_HINT} value={reason} onChange={setReason} error={error} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="button" className={`btn btn--primary${saving ? ' is-loading' : ''}`} onClick={onSave} disabled={saving}>
          <i className="fa-solid fa-floppy-disk" aria-hidden="true" /> Save changes
        </button>
      </div>
    </div>
  );
}

// ── section editors ───────────────────────────────────────────────────────────
interface EditorProps { record: ChangeRequest; save: Save; saving: boolean; errors: Errors; onCancel: () => void }

function Section1({ record, save, saving, errors, onCancel }: EditorProps) {
  const { showToast } = useToast();
  const [changeName, setChangeName] = useState(record.changeName);
  const [sponsor, setSponsor] = useState({ name: record.sponsorName, email: record.sponsorEmail });
  const [scope, setScope] = useState(record.scope);
  const [approvers, setApprovers] = useState<Approver[]>(safeJson<Approver[]>(record.approvers, []));
  const [goLive, setGoLive] = useState((record.proposedGolive ?? '').slice(0, 10));
  const [reason, setReason] = useState('');
  const multi = scope === 'Multi Dept';

  function addApprover(u: GraphUser) {
    if (approvers.some(a => a.email.toLowerCase() === u.userPrincipalName.toLowerCase())) { showToast(`${u.displayName} is already an approver.`, 'warn'); return; }
    setApprovers(multi ? [...approvers, { name: u.displayName, email: u.userPrincipalName }] : [{ name: u.displayName, email: u.userPrincipalName }]);
  }

  return (
    <>
      <div className="form-grid">
        <div className="form-group form-group--full">
          <label className="form-label" htmlFor="amend-name">Change name <span className="required">*</span></label>
          <input id="amend-name" className={`form-control${errors.changeName ? ' is-error' : ''}`} value={changeName} onChange={e => setChangeName(e.target.value)} />
          <FieldError message={errors.changeName} />
        </div>
        <div className="form-group">
          <label className="form-label">Change sponsor <span className="required">*</span></label>
          <PersonPill name={sponsor.name} detail={sponsor.email} />
          <div style={{ marginTop: 8 }}>
            <PeoplePicker placeholder="Search to replace the sponsor…" onSelect={u => setSponsor({ name: u.displayName, email: u.userPrincipalName })} />
          </div>
          <FieldError message={errors.sponsor} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="amend-golive">Proposed go-live date <span className="required">*</span></label>
          <input id="amend-golive" type="date" className={`form-control${errors.proposedGolive ? ' is-error' : ''}`} value={goLive} onChange={e => setGoLive(e.target.value)} />
          <FieldError message={errors.proposedGolive} />
        </div>
        <div className="form-group form-group--full">
          <label className="form-label">Scope <span className="required">*</span></label>
          <Pills options={SCOPES} value={scope} onChange={v => { setScope(v); if (v !== 'Multi Dept') setApprovers(a => a.slice(0, 1)); }} />
          <FieldError message={errors.scope} />
        </div>
        <div className="form-group form-group--full">
          <label className="form-label">{multi ? 'Approvers' : 'Approver'} <span className="required">*</span></label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
            {approvers.map((a, i) => (
              <PersonPill key={a.email} name={a.name} detail={a.email} onRemove={approvers.length > 1 || multi ? () => setApprovers(approvers.filter((_, j) => j !== i)) : undefined} />
            ))}
          </div>
          <PeoplePicker placeholder={multi ? 'Search to add an approver…' : 'Search to replace the approver…'} onSelect={addApprover} />
          <FieldError message={errors.approvers} />
        </div>
      </div>
      <SaveBar reason={reason} setReason={setReason} error={errors.reason} saving={saving} onCancel={onCancel}
        onSave={() => void save({ changeName, sponsorName: sponsor.name, sponsorEmail: sponsor.email, scope, approvers, proposedGolive: goLive }, reason)} />
    </>
  );
}

function Section2({ record, save, saving, errors, onCancel }: EditorProps) {
  const [currentState, setCurrentState] = useState(record.currentState);
  const [newState, setNewState] = useState(record.newState);
  const [benefitsReason, setBenefitsReason] = useState(record.benefitsReason);
  const [reason, setReason] = useState('');
  return (
    <>
      <div className="form-grid">
        <TextArea id="amend-current" label="Current state" required value={currentState} onChange={setCurrentState} error={errors.currentState} />
        <TextArea id="amend-new" label="New state" required value={newState} onChange={setNewState} error={errors.newState} />
        <TextArea id="amend-benefits" label="Benefits / reason for change" required value={benefitsReason} onChange={setBenefitsReason} error={errors.benefitsReason} />
      </div>
      <SaveBar reason={reason} setReason={setReason} error={errors.reason} saving={saving} onCancel={onCancel}
        onSave={() => void save({ currentState, newState, benefitsReason }, reason)} />
    </>
  );
}

function Section3({ record, save, saving, errors, onCancel }: EditorProps) {
  const [riskIfImplemented, setImpl] = useState(record.riskIfImplemented);
  const [riskIfNotImplemented, setNotImpl] = useState(record.riskIfNotImplemented);
  const [reason, setReason] = useState('');
  return (
    <>
      <div className="form-grid">
        <TextArea id="amend-risk-impl" label="What could go wrong if this change is implemented?" required rows={6} value={riskIfImplemented} onChange={setImpl} error={errors.riskIfImplemented} />
        <TextArea id="amend-risk-not" label="What could happen if this change is NOT implemented?" required rows={5} value={riskIfNotImplemented} onChange={setNotImpl} error={errors.riskIfNotImplemented} />
      </div>
      <SaveBar reason={reason} setReason={setReason} error={errors.reason} saving={saving} onCancel={onCancel}
        onSave={() => void save({ riskIfImplemented, riskIfNotImplemented }, reason)} />
    </>
  );
}

function Section4({ record, save, saving, errors, onCancel }: EditorProps) {
  const [dependencies, setDependencies] = useState(record.dependencies ?? '');
  const [reason, setReason] = useState('');
  return (
    <>
      <div className="form-grid">
        <TextArea id="amend-deps" label="Dependencies" rows={6} value={dependencies} onChange={setDependencies} error={errors.dependencies}
          hint="Optional. Other changes, approvals, decisions or deliveries this change relies on." />
      </div>
      <SaveBar reason={reason} setReason={setReason} error={errors.reason} saving={saving} onCancel={onCancel}
        onSave={() => void save({ dependencies }, reason)} />
    </>
  );
}

function Section5({ record, save, saving, errors, onCancel }: EditorProps) {
  const [external, setExternal] = useState(record.externalStakeholders ?? '');
  const [policyUpdate, setPolicyUpdate] = useState<string>(record.policyDocsUpdateRequired);
  const [docs, setDocs] = useState(record.policyDocsToUpdate ?? '');
  const [reason, setReason] = useState('');
  return (
    <>
      <div className="form-grid">
        <TextArea id="amend-external" label="External stakeholders affected" rows={3} value={external} onChange={setExternal} error={errors.externalStakeholders} hint="Optional." />
        <div className="form-group form-group--full">
          <label className="form-label">Does this change require policy or procedure updates? <span className="required">*</span></label>
          <Pills options={['Yes', 'No']} value={policyUpdate} onChange={setPolicyUpdate} />
          <FieldError message={errors.policyDocsUpdateRequired} />
        </div>
        {policyUpdate === 'Yes' && (
          <TextArea id="amend-docs" label="Documents to be updated" required rows={6} value={docs} onChange={setDocs} error={errors.policyDocsToUpdate} />
        )}
      </div>
      <SaveBar reason={reason} setReason={setReason} error={errors.reason} saving={saving} onCancel={onCancel}
        onSave={() => void save({ externalStakeholders: external, policyDocsUpdateRequired: policyUpdate, policyDocsToUpdate: docs }, reason)} />
    </>
  );
}

function Section6({ record, save, saving, errors, onCancel, responses }: EditorProps & { responses: StakeholderResponse[] }) {
  const { showToast } = useToast();
  const [list, setList] = useState<AffectedStakeholder[]>(safeJson<AffectedStakeholder[]>(record.affectedStakeholders, []));
  const [reason, setReason] = useState('');
  const original = new Set(safeJson<AffectedStakeholder[]>(record.affectedStakeholders, []).map(s => s.email.toLowerCase()));
  // Someone who has already responded or signed off cannot be taken off the change: their record has to stay.
  const locked = (email: string) => responses.some(r => r.teamLeadEmail.toLowerCase() === email.toLowerCase() && (r.responseStatus !== 'Pending' || r.signoffStatus !== 'Pending'));

  function add(u: GraphUser) {
    if (list.some(s => s.email.toLowerCase() === u.userPrincipalName.toLowerCase())) { showToast(`${u.displayName} is already added.`, 'warn'); return; }
    setList([...list, { name: u.displayName, email: u.userPrincipalName, department: u.department ?? '' }]);
  }
  const added = list.filter(s => !original.has(s.email.toLowerCase())).length;

  return (
    <>
      <PeoplePicker onSelect={add} placeholder="Search to add a stakeholder…" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        {list.map(s => {
          const isNew = !original.has(s.email.toLowerCase());
          return (
            <div key={s.email} className="stakeholder-row">
              <div className="stakeholder-row__avatar">{s.name.charAt(0).toUpperCase()}</div>
              <div className="stakeholder-row__info">
                <div className="stakeholder-row__name">{s.name} {isNew && <span className="badge badge--accent">New</span>} {locked(s.email) && <span className="badge badge--muted">Responded</span>}</div>
                <div className="stakeholder-row__meta">{s.email}{s.department ? ` · ${s.department}` : ''}</div>
              </div>
              {!locked(s.email) && (
                <button type="button" className="stakeholder-row__remove" onClick={() => setList(list.filter(x => x.email !== s.email))} aria-label={`Remove ${s.name}`}>
                  <i className="fa-solid fa-trash-can" aria-hidden="true" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <FieldError message={errors.affectedStakeholders} />
      {added > 0 && (
        <div className="alert alert--info" style={{ marginTop: 12 }}>
          <div className="alert__body">Each new stakeholder is sent their response request as soon as you save.</div>
        </div>
      )}
      <SaveBar reason={reason} setReason={setReason} error={errors.reason} saving={saving} onCancel={onCancel}
        onSave={() => void save({ affectedStakeholders: list }, reason)} />
    </>
  );
}

// ── implementation plan draft ─────────────────────────────────────────────────
function PlanDraft({ record, cid, onSaved }: { record: ChangeRequest; cid: string; onSaved: () => Promise<void> }) {
  const api = useApiClient();
  const { showToast } = useToast();
  const existing = safeJson<Partial<ImplementationPlan>>(record.implementationPlan, {});
  const [approach, setApproach] = useState(existing.approach ?? '');
  const [milestones, setMilestones] = useState(existing.milestones ?? '');
  const [training, setTraining] = useState(existing.trainingRequirements ?? '');
  const [comms, setComms] = useState(existing.communicationPlan ?? '');
  const [risks, setRisks] = useState(existing.risks ?? '');
  const [resources, setResources] = useState(existing.resourceCosts ?? '');
  const [goLive, setGoLive] = useState(existing.goLiveDate ?? '');
  const [stabilisation, setStabilisation] = useState(existing.stabilisationPeriod ?? '');
  const [saving, setSaving] = useState(false);

  async function saveDraft() {
    setSaving(true);
    try {
      await api.patch(`/changes/${encodeURIComponent(cid)}/plan-draft`, {
        implementationPlan: { approach, milestones, trainingRequirements: training, communicationPlan: comms, risks, resourceCosts: resources, goLiveDate: goLive, stabilisationPeriod: stabilisation },
      });
      showToast('Plan draft saved. It will be waiting for you at the Plan step.', 'success');
      await onSaved();
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="alert alert--info" style={{ marginBottom: 16 }}>
        <div className="alert__body">
          A draft is private to you: nothing is sent and the change does not move forward. When the change reaches the Plan step this form
          opens already filled in, and submitting it from there requests sign-off.
          {existing.draft && existing.draftSavedOn && <> <strong>Last draft saved {fmtDateTime(existing.draftSavedOn)}{existing.draftSavedBy ? ` by ${existing.draftSavedBy}` : ''}.</strong></>}
        </div>
      </div>
      <div className="form-grid">
        <div className="form-group form-group--full">
          <label className="form-label">Implementation approach</label>
          <Pills options={APPROACHES} value={approach} onChange={setApproach} />
        </div>
        <TextArea id="draft-milestones" label="Key activities and milestones" value={milestones} onChange={setMilestones}
          hint="For example the steps and dates from deployment to the go/no-go decision." />
        <TextArea id="draft-training" label="Training and support requirements" value={training} onChange={setTraining} />
        <TextArea id="draft-comms" label="Communication plan" value={comms} onChange={setComms} />
        <TextArea id="draft-risks" label="Risks and mitigations (including rollback)" value={risks} onChange={setRisks} />
        <TextArea id="draft-resources" label="Resource and cost requirements" value={resources} onChange={setResources} />
        <div className="form-group">
          <label className="form-label" htmlFor="draft-golive">Go-live date</label>
          <input id="draft-golive" type="date" className="form-control" value={goLive} onChange={e => setGoLive(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="draft-stab">Stabilisation period</label>
          <input id="draft-stab" className="form-control" value={stabilisation} placeholder="e.g. 4 weeks" onChange={e => setStabilisation(e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className={`btn btn--primary${saving ? ' is-loading' : ''}`} onClick={() => void saveDraft()} disabled={saving}>
          <i className="fa-solid fa-floppy-disk" aria-hidden="true" /> Save draft
        </button>
      </div>
    </>
  );
}

// ── history ───────────────────────────────────────────────────────────────────
export function AmendmentList({ amendments }: { amendments: ChangeAmendment[] }) {
  if (amendments.length === 0) return <p className="text-muted">This change has not been amended since it was submitted.</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {amendments.map(a => {
        const changes = safeJson<FieldChange[]>(a.changes, []);
        return (
          <div key={a.id} className="card card--flat" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <strong>Section {a.sectionNumber}: {a.sectionTitle}</strong>
              <span className="text-muted" style={{ fontSize: '0.8125rem' }}>{a.amendedByName} · {fmtDateTime(a.amendedOn)}</span>
            </div>
            {a.afterSignoff === 'Yes' && (
              <div className="alert alert--warn" style={{ marginTop: 8 }}>
                <div className="alert__body">Made while the change was <strong>{a.statusAtTime}</strong>, after stakeholders or approvers had been asked to decide.</div>
              </div>
            )}
            <p style={{ margin: '8px 0' }}><strong>Reason:</strong> {a.reason}</p>
            {changes.map(c => (
              <div key={c.field} style={{ marginTop: 8 }}>
                <div className="field-display__label">{c.label}</div>
                <div style={{ color: 'var(--danger, #b91c1c)', textDecoration: 'line-through', whiteSpace: 'pre-wrap' }}>{c.from}</div>
                <div style={{ color: 'var(--success, #166534)', whiteSpace: 'pre-wrap' }}>{c.to}</div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────
interface SectionDef { n: number; title: string; icon: string; summary: (r: ChangeRequest) => string }
const SECTIONS: SectionDef[] = [
  { n: 1, title: 'Change Details', icon: 'fa-file-signature', summary: r => `${r.changeName} · ${r.scope} · go-live ${fmtDate(r.proposedGolive)}` },
  { n: 2, title: 'Change Description', icon: 'fa-align-left', summary: r => r.newState.slice(0, 120) },
  { n: 3, title: 'Risk Consideration', icon: 'fa-triangle-exclamation', summary: r => r.riskIfImplemented.slice(0, 120) },
  { n: 4, title: 'Dependencies', icon: 'fa-link', summary: r => r.dependencies ? r.dependencies.slice(0, 120) : 'Nothing entered yet' },
  { n: 5, title: 'External Stakeholders & Policy Updates', icon: 'fa-file-contract', summary: r => `Policy updates: ${r.policyDocsUpdateRequired}${r.externalStakeholders ? ' · external stakeholders listed' : ''}` },
  { n: 6, title: 'Affected Stakeholders', icon: 'fa-user-group', summary: r => safeJson<AffectedStakeholder[]>(r.affectedStakeholders, []).map(s => s.name).join(', ') || 'None' },
];

export default function AmendChange() {
  const [searchParams] = useSearchParams();
  const cid = searchParams.get('cid') ?? '';
  const { userEmail } = useAuth();
  const api = useApiClient();
  const { showToast } = useToast();

  const [record, setRecord] = useState<ChangeRequest | null>(null);
  const [history, setHistory] = useState<AmendmentsResponse | null>(null);
  const [responses, setResponses] = useState<StakeholderResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [open, setOpen] = useState<number | 'plan' | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const load = useCallback(async () => {
    try {
      const [rec, hist, resp] = await Promise.all([
        api.get<ChangeRequest>(`/changes/${encodeURIComponent(cid)}`),
        api.get<AmendmentsResponse>(`/changes/${encodeURIComponent(cid)}/amendments`),
        api.get<StakeholderResponse[]>(`/changes/${encodeURIComponent(cid)}/responses`),
      ]);
      setRecord(rec); setHistory(hist); setResponses(resp);
    } catch (err) {
      setProblem((err as Error).message || 'The change record could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => { if (cid) void load(); else { setProblem('No change was specified.'); setLoading(false); } }, [cid, load]);

  const save = (section: number): Save => async (values, reason) => {
    setSaving(true); setErrors({});
    try {
      const res = await api.patch<{ changed: boolean; afterSignoff?: boolean }>(`/changes/${encodeURIComponent(cid)}/amend`, { section, values, reason });
      if (!res.changed) showToast('Nothing was different, so nothing was saved.', 'info');
      else showToast(res.afterSignoff ? 'Saved. Approvers and stakeholders have been told this was changed after sign-off.' : 'Saved and recorded. The people on this change have been notified.', 'success');
      await load();
      setOpen(null);
      return true;
    } catch (err) {
      const e = err as ApiRequestError;
      setErrors(e.errors ?? {});
      showToast(e.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Layout><div className="loading-state"><div className="spinner spinner--lg" role="status" aria-label="Loading" /><span>Loading…</span></div></Layout>;

  if (problem || !record || !history) return (
    <Layout>
      <div className="card"><div className="state-card">
        <div className="state-card__icon"><i className="fa-solid fa-circle-question" aria-hidden="true" /></div>
        <h2 className="state-card__title">Cannot open this change</h2>
        <p className="state-card__body">{problem || 'The change record could not be found.'}</p>
        <Link to="/" className="btn btn--secondary" style={{ marginTop: 8 }}>Back to Home</Link>
      </div></div>
    </Layout>
  );

  const isLead = record.leadEmail.toLowerCase() === userEmail.toLowerCase();
  const statusOk = AMENDABLE_STATUSES.includes(record.status);
  const editable = history.editable && statusOk;
  const afterSignoff = POST_SIGNOFF_STATUSES.includes(record.status);
  const canDraftPlan = editable && PLAN_DRAFT_STATUSES.includes(record.status);

  const closeEditor = () => { setOpen(null); setErrors({}); };
  const editorFor = (n: number) => {
    const props = { record, save: save(n), saving, errors, onCancel: closeEditor };
    switch (n) {
      case 1: return <Section1 {...props} />;
      case 2: return <Section2 {...props} />;
      case 3: return <Section3 {...props} />;
      case 4: return <Section4 {...props} />;
      case 5: return <Section5 {...props} />;
      default: return <Section6 {...props} responses={responses} />;
    }
  };

  return (
    <Layout>
      <div className="page-header">
        <div className="page-header__title">
          <div className="breadcrumb">
            <Link to="/" className="breadcrumb__link"><i className="fa-solid fa-house" aria-hidden="true" /> Home</Link>
            <span className="breadcrumb__sep">/</span>
            <Link to={`/change-detail?cid=${encodeURIComponent(cid)}`} className="breadcrumb__link">{record.changeId}</Link>
            <span className="breadcrumb__sep">/</span>
            <span className="breadcrumb__current">Edit submission</span>
          </div>
          <h1>Edit submission</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
            <span className={`badge ${statusBadgeClass(record.status)}`}>{record.status}</span>
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>{record.changeId} · {record.changeName}</span>
          </div>
        </div>
        <div className="page-header__actions">
          {isLead && <Link to={`/manage-change?cid=${encodeURIComponent(cid)}`} className="btn btn--secondary btn--sm"><i className="fa-solid fa-arrow-left" aria-hidden="true" /> Back to Manage Change</Link>}
        </div>
      </div>

      {!isLead && (
        <div className="alert alert--warn" style={{ marginBottom: 16 }}><div className="alert__body">Only the Change Lead ({record.leadName}) can edit this submission. You can read it and its history here.</div></div>
      )}
      {isLead && !statusOk && (
        <div className="alert alert--warn" style={{ marginBottom: 16 }}>
          <div className="alert__body"><strong>This change is {record.status}, so its submission is locked.</strong> Once a change is approved, what was approved must stay exactly as it was approved.</div>
        </div>
      )}
      {editable && afterSignoff && (
        <div className="alert alert--warn" style={{ marginBottom: 16 }}>
          <div className="alert__body"><strong>Stakeholders or approvers have already been asked to decide on this change.</strong> You can still correct it. Your edit is recorded, and they are told it was changed, so they can review it before relying on an earlier response.</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {SECTIONS.map(s => (
          <div key={s.n} className="section-panel">
            <div className="section-panel__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <h2 className="section-panel__title"><i className={`fa-solid ${s.icon}`} aria-hidden="true" /> Section {s.n}: {s.title}</h2>
                {open !== s.n && <p className="text-muted" style={{ margin: '4px 0 0', fontSize: '0.875rem' }}>{s.summary(record)}</p>}
              </div>
              {editable && open !== s.n && (
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setOpen(s.n); setErrors({}); }}>
                  <i className="fa-solid fa-pen" aria-hidden="true" /> {s.n === 4 && !record.dependencies ? 'Add' : 'Edit'}
                </button>
              )}
            </div>
            {open === s.n && <div className="section-panel__body">{editorFor(s.n)}</div>}
          </div>
        ))}

        {canDraftPlan && (
          <div className="section-panel">
            <div className="section-panel__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <h2 className="section-panel__title"><i className="fa-solid fa-clipboard-list" aria-hidden="true" /> Implementation plan (draft)</h2>
                {open !== 'plan' && <p className="text-muted" style={{ margin: '4px 0 0', fontSize: '0.875rem' }}>Write the plan now; it is submitted for sign-off later, at the Plan step.</p>}
              </div>
              {open !== 'plan' && (
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => setOpen('plan')}>
                  <i className="fa-solid fa-pen" aria-hidden="true" /> {safeJson<Partial<ImplementationPlan>>(record.implementationPlan, {}).draft ? 'Edit draft' : 'Start draft'}
                </button>
              )}
            </div>
            {open === 'plan' && <div className="section-panel__body"><PlanDraft record={record} cid={cid} onSaved={async () => { await load(); }} /></div>}
          </div>
        )}

        <div className="section-panel">
          <div className="section-panel__header"><h2 className="section-panel__title"><i className="fa-solid fa-clock-rotate-left" aria-hidden="true" /> Amendment history</h2></div>
          <div className="section-panel__body"><AmendmentList amendments={history.amendments} /></div>
        </div>
      </div>
    </Layout>
  );
}
